/*
    I describe how to get and interact with elements based on high-level
    user-centric names of things (e.g., "patient search"). I'm used for driving
    integration tests.

    The 'elements' key stores selectors for semantically identifying elements
    in the DOM.  Each level must be an object with a 'selector' key for the
    CSS path the element(s) in question.  The object may have additional keys
    which semantically identify subelements.  In all cases the key is the
    semantic name (converted to camel-case) of the element.

    The 'actions' key stores functions for taking actions on a given element.
    These should be fail-fast where appropriate to help test authors debug
    errors they may have made.  These cannot return values, use an inspection
    for that.

    The 'inspections' key stores functions for inspecting a given element.
    These should be fail-fast where appropriate to help test authors debug
    errors they may have made.  These may not mutate the state of the element,
    use an action for that.

    Actions and inspections can both accept arguments beyond the element to act
    upon and tests will be forced to provide the correct number of arguments.
    Thus they cannot use optional arguments.  Sorry.
*/

import entityMetaStore from "data/meta/entityMetaStore";
import lockClient from "mock/lib/lockClient";

const ReactTestUtils = require("react-dom/test-utils");

const augment = function(base, ...others) {
    for (const other of others) {
        for (const key of Object.keys(other)) {
            if (base[key] == null) {
                base[key] = other[key];
            }
        }
    }
    return base;
};

const recordGridChildren = {
    topadd: {
        selector: "thead > tr > th.actions button",
        caret: {
            selector: ".caret",
        },
    },
    bottomadd: {
        selector: ".bottom-button-row button"
    },
    rows: {
        selector: "tbody tr",
        add: {
            selector: "button.btn-add",
        },
        delete: {
            selector: "button.btn-remove",
        },
        view: {
            selector: "button.btn-view",
        },
    },
};

const controlSelect = function ($el, key) {
    const block =
        {selector: `.control-container-${key}`};

    const $tgt = $el.find(block.selector);
    switch ($tgt.data("controlType")) {
        case "recordbrowser":
            block.browse = {
                selector: ".icon-search"
            };
            break;
        case "typeahead":
            block.browse = {
                selector: ".icon-search"
            };
            block.clear = {
               selector: ".clear-value"
            };
            block.selectAll = {
               selector: ".select-all"
            };
            break;
        case "summarysection":
            Object.assign(block, recordGridChildren);
            // also need control nesting
            block.rows.control = controlSelect;
            break;
        case "devicelesiongrid":
            Object.assign(block, recordGridChildren);
            // also need control nesting
            block.rows.control = controlSelect;
            block.rows.checkbox = ($el, key) => ({
                selector: `.control.checkbox-field[data-shortname=${key}]`,
            });
            break;

        case "calendar":
        case "spinner":
            block.dropdown =
                {selector: ".calendar-button"};
            break;
        case "manager":
            block.files = {
                selector: ".file-link"
            };
            block.remove = {
                selector: ".file-remove"
            };
            break;
        default:
            // no type-specific augmentation needed
    }

    return block;
};

const searchFormControl = ($el, key) => {
    let selector = `.control-container-${key}`;
    if ($el.find(selector + "val").length){
        selector += "val";
    }
    return {
        selector,
    };
};

const asSplitButton = sel => ({
    primary: {
        selector: sel.replace(/\./g, ".primary-"),
    },
    dropdown: {
        selector: sel.replace(/\./g, ".split-"),
    },
});

const searchBlockBuilder = (searchButtonSelector=".btn-search") => ({
    selector: ".drawer-body",
    form: {
        selector: ".search-form-layout",
        control: searchFormControl,

        inputField: {
            selector: ".search-inputs .control:first"
        },
        typeField: {
            selector: "#types .control"
        },
        advancedSearchToggle: {
            selector: ".advanced-search-toggle"
        },
        relativeDateToggle: {
            selector: ".togglerelativedates",
        },
        searchButton: {
            selector: searchButtonSelector,
            ...asSplitButton(searchButtonSelector),
        },
        noSavedSearch: {
            selector: ".no-saved-search",
        },
        newButton: {
            selector: ".btn-create"
        },
        clearSearch: {
            selector: ".clear"
        }
    },
    showDisabled: {
        selector: ".showDisabled",
    },
    headers: {
        selector: ".search-results thead tr th",
    },
    results: {
        selector: ".search-results tbody tr.selection-table-row",
        fields: {
            selector: "td"
        }
    },
    noResults: {
        selector: ".search-results tbody tr#nosearchresults_row"
    },
});

const fieldAdminPaneBuilder = selector => {
    const sectionSkeleton = {
        heading: {
            selector: ".panel-heading",
        },
        items: {
            selector: ".list-group-item",
        },
    };
    return {
        selector,
        sections: {
            selector: ".list-group",
            ...sectionSkeleton,
        },
        section($el, key) {
            return {
                selector: `.list-group[data-group='${key}']`,
                ...sectionSkeleton,
                item($el, key) {
                    return {
                        selector: `.list-group-item[data-key='${key}']`,
                    };
                },
            };
        },
    };
};

const modalSkeleton = {
    selector: "#modal .modal-content",
    title: {
        selector: ".modal-title"
    },
    close: {
        selector: ".modal-close"
    },
};

const elements = {
    dropdownMenu: {
      selector: "body .dropdown-wrapper .dropdown-menu"
    },
    navbar: {
        selector: "#global-navbar",
        hybrid: {
            selector: ".menu-hybrid"
        },
        settings: {
            selector: ".menu-settings",
        },
        orgs: {
            selector: ".menu-orgs"
        },
        userMenu: {
            selector: ".menu-user",
            toggle: {
                selector: ".dropdown-toggle"
            },
            logout: {
                selector: ".logout-link"
            }
        }
    },

    dataEntry: {
        selector: "#content_de",
        search: searchBlockBuilder(".search-button"),
        drawerPull: {
            selector: ".btn-drawer-pull"
        },
        drawer: {
            selector: ".drawer-container",
        },
        openRecord: {
            selector: ".drawer-content",
            headerBar: {
                selector: ".header h1",
                label: {
                    selector: ".entity-label"
                },
                detail: {
                    selector: ".entity-detail"
                },
                delete: {
                    selector: "button.deleteRoot"
                },
                merge: {
                    selector: "button.mergeRoot"
                },
                pesslockWarning: {
                    selector: ".pesslock-warning",
                },
            },
            breadcrumb: {
                selector: ".header .button-group",
                root: {
                    selector: ".root",
                    button: {
                        selector: "a.entity"
                    },
                    rootMenu: {
                        selector: ".dropdown-toggle"
                    },
                    delete: {
                        selector: ".deleteRoot"
                    }
                },
                list: {
                    selector: ".navigator.btn-group > span"
                }
            },
            navigatorColumns: {
                selector: ".entity.navigator .column",
                header: {
                    selector: "header .column-header"
                },
                add: {
                    selector: "header .btn-add"
                },
                remove: {
                    selector: "header .btn-remove"
                },
                itemList: {
                    selector: ".items",
                    items: {
                        selector: "li:visible"
                    }
                }
            },
            bottomPane: {
                selector: ".bottom-pane",
                saveButton: {
                    selector: "button.actionSave"
                },
                cancelButton: {
                    selector: "button.actionCancel"
                },
                mapButton: {
                    selector: "button.btn-mapping"
                },
                mapButtonDropDown: {
                    selector: "button.btn-mapping-dropdown"
                },
                mergeButton: {
                    selector: "button.btn-merge"
                },
                mergeButtonDropDown: {
                    selector: "button.btn-merge-dropdown"
                },
                printButton: {
                    selector: "button.btn-print",
                },
                jumpTo: {
                    selector: ".form-section"
                },
                versionDropDown: {
                    selector: ".version-section"
                },
                form: {
                    selector: ".de_pane .entity-information",
                    control: controlSelect,
                    widget($el, label) {
                        const block = {
                            selector: `[data-widget-label="${label.replace(/"/g, "\\\"")}"]`
                        };
                        const $tgt = $el.find(block.selector);
                        switch ($tgt.data("widgetType")) {
                            case "magicrootsummarysection":
                                Object.assign(block, recordGridChildren);
                                break;
                            default:
                               // no type-specific augmentation needed
                        }
                        return block;
                    },
                    vjsPlayer: {
                        selector: ".video-js"
                    },
                    modeSelector: {
                        selector: ".mode-selector",
                        individualButton: {
                            selector: ".btn[value=individual]"
                        },
                        comparisonButton: {
                            selector: ".btn[value=comparison]"
                        }
                    },
                    comparisonSelector: {
                        selector: ".dropdown-container"
                    }
                },
                definitionsTab: {
                    selector: ".sidebar .navigation li.definitions a"
                },

                dataDefinitions: {
                    selector: ".sidebar .data-definitions",
                    header: {
                        selector: ".panel-heading"
                    },
                    keyboardShortcuts: {
                        selector: ".keyboard-shortcuts li"
                    }
                },

                statusBar: {
                    selector: ".form.footer .msg"
                },

                validationTab: {
                    selector: ".sidebar .navigation li.validation a"
                },

                validationPanel: {
                    selector: ".sidebar .validation-panel",
                    filter($el, key) {
                        const block =
                            {selector: `.${key}`};

                        return block;
                    },

                    panelBody: {
                        selector: ".panel-body",

                        tree: {
                            selector: ".validationtree",

                            item($el, key) {
                                return {
                                    selector: `.tree-item-${key}`,
                                    status: {
                                        selector: ".indicator-container",
                                    },
                                    expander: {
                                        selector: ".expander",
                                    },
                                    message: {
                                        selector: ".message",
                                    },
                                };
                            },
                        }
                    }
                }
            }
        }
    },

    modalPrompt: {
        selector: ".modal-prompt",
        value: {
            selector: ".control-container-value",
        },
        ok: {
            selector: "button.btn-primary",
        },
        cancel: {
            selector: "button.btn-default",
        },
    },

    loadingModal: {
        selector: ".loading-box",
        message: {
            selector: ".loading-body"
        },
        // these are grandfathered in
        no: {
            selector: "[value^=No]"
        },
        yes: {
            selector: "[value^=Yes]"
        },
        ok: {
            selector: "[value^=Ok]"
        },
        cancel: {
            selector: "[value^=Cancel]"
        },
        button($el, prefix) {
            return {
                selector: `[value^="${prefix}"]`,
            };
        },
    },

    globalModal: {
        ...modalSkeleton,
    },

    gxepModal: {
        ...modalSkeleton,
        save: {
            selector: ".btn-save",
        },
        cancel: {
            selector: ".btn-cancel",
        },
    },

    preferencesDialog: {
        ...modalSkeleton,
    },

    printDialog: {
        selector: "#modal .modal-content",
        printButton: {
            selector: ".modal-footer .btn-primary"
        },
        cancelButton: {
            selector: ".modal-footer .btn-secondary"
        },
        form: {
            selector: "form",
            control: controlSelect,
        },
    },

    gxepDialog: {
        selector: "#modal .modal-content",
        saveButton: {
            selector: ".btn-save",
        },
        cancelButton: {
            selector: ".btn-cancel",
        },
    },

    mappingDialog: {
        ...modalSkeleton,
        search: {
            ...searchBlockBuilder(),
            source: {
                selector: ".control-container-source-type"
            },
        },
        drawerPull: {
            selector: ".btn-drawer-pull",
        },
        actionBar: {
            selector: ".mapping-action-bar",
            import: {
                selector: ".btn-import"
            },
            cancel: {
                selector: ".btn-cancel"
            }
        },
        openRecord: {
            selector: ".drawer-content",
            targetLabel: {
                selector: ".mapping-header div.col-md-12"
            },
            includeFields: {
                selector: ".include-fields"
            },
            excludeFields: {
                selector: ".exclude-fields"
            },
            showMissingFields: {
                selector: ".show-fields"
            },
            hideMissingFields: {
                selector: ".hide-fields"
            },
            results: {
                selector: ".mapping-grid",
                fields: {
                    selector: "[data-shortname]",
                    checked: {
                        selector: "label .checked"
                    }
                },
                field($el, key) {
                    const block = {
                        selector: `[data-shortname='${key}']`,
                        label: {
                            selector: ".target-entity-heading label"
                        },
                        newValue: {
                            selector: ".new-value"
                        },
                        oldValue: {
                            selector: ".old-value"
                        },

                        toggleState: {
                            selector: ".toggle-state"
                        }
                    };

                    return block;
                }
            }
        }
    },

    mergeDialog: {
        // this will be augmented with .mappingDialog, except .actionBar
        openRecord: {
            // this will be augmented with .mappingDialog.openRecord
            merge: {
                selector: ".btn-merge",
            },
            cancelMerge: {
                selector: ".btn-cancel",
            },
        },
    },

    recordBrowser: {
        ...modalSkeleton,
        selector: "#modal .record-browser .modal-content",
    },

    hex: {
        selector: "#content_hex",
        drawerPull: {
            selector: ".btn-drawer-pull"
        },
        search: {
            selector: ".drawer-body",
            exportType: {
                selector: ".export-type-select",
            },
            paramSets: {
                selector: ".param-sets",
                select: {
                    selector: ".param-set-select"
                },
                rename: {
                    selector: ".param-set-rename"
                },
                delete: {
                    selector: ".param-set-delete"
                },
            },
            dataOwners: {
                selector: ".data-owner-select",
            },
            armusXmlExport: {
                selector: ".generic-xml-export",
                stereotype: {
                    selector: ".form-section-header .control-container-stereotype",
                },
                control: searchFormControl,
            },
            exportButton: {
                selector: ".btn-do-export button:first-child",
            },
            exportDropdown: {
                selector: ".btn-do-export button:last-child",
            },
        },
        header: {
            selector: ".search-results thead tr",
            fields: {
                selector: "th"
            }
        },
        results: {
            selector: ".search-results tbody tr",
            fields: {
                selector: "td"
            }
        }
    },

    dqr: {
        selector: "#content_dqr",
        drawerPull: {
            selector: ".btn-drawer-pull"
        },
        search: searchBlockBuilder(),

        header: {
            selector: ".search-results thead tr",
            fields: {
                selector: "th"
            }
        },
        results: {
            selector: ".search-results tbody tr",
            fields: {
                selector: "td"
            }
        }
    },

    fieldsetAdmin: {
        selector: "#two-pane-multi-select",
        unselected: fieldAdminPaneBuilder("#unselected-list"),
        selected: fieldAdminPaneBuilder("#selected-list"),
        save: {
            selector: ".btn-save",
        },
    }
};

augment(elements.recordBrowser, elements.dataEntry);
augment(elements.globalModal, elements.dataEntry);
augment(elements.mergeDialog, elements.mappingDialog);
delete elements.mergeDialog.actionBar;
augment(elements.mergeDialog.openRecord, elements.mappingDialog.openRecord);

const isReactElement = (el) =>
    // tests if an element is controlled by react fiber.
    // this is pretty hacky but works our case.
    Object.keys(el).filter(it => it.indexOf("__react") !== -1).length > 0;

const getVisibleInput = function($el) {
    const item = $el.find(":input:visible");
    if (item.length === 0) {
        throw new Error(`Failed to find a :input w/in control (${$el.selector}); AppDriver needs augmentation`);
    }
    return item;
};

const performClick = function($el, type = "click") {
    if ($el.is(":disabled")) {
        throw new Error($el.selector + " is disabled");
    }
    const activeElement = document.activeElement;

    // first comes mouse down
    const downEvent = document.createEvent("MouseEvents");
    downEvent.initEvent("mousedown", true, true);
    $el.get(0).dispatchEvent(downEvent);

    // then blur
    $(activeElement).blur();

    // then click
    const clickEvent = document.createEvent("MouseEvents");
    clickEvent.initEvent(type, true, true);
    $el.get(0).dispatchEvent(clickEvent);

    // finally mouse up
    const upEvent = document.createEvent("MouseEvents");
    upEvent.initEvent("mouseup", true, true);
    $el.get(0).dispatchEvent(upEvent);
};

const performChange = function($el, value) {
    // .change() is not enough to trigger react lifecycle change
    let $elem = $el.first();
    if(!$el.is("input, select, textarea, button")) {
        $elem = $el.find("input, select, textarea, button").first();
    }
    if(isReactElement($elem.get(0))) {
        // react element
        ReactTestUtils.Simulate.focus($elem.get(0));
        ReactTestUtils.Simulate.change($elem.get(0), {target: {value: value, checked: !!value}});
        ReactTestUtils.Simulate.blur($elem.get(0)); // triggers onCommitChange
    }
    else {
        // vanilla element
        $elem.focus();
        $elem.val(value); // set the controls input value.
        $elem.change();
        $elem.blur();
    }
};

// don't make up your own names. please. use these:
// https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values
const KEY_MAP = {
    Backspace: 8,
    Tab: 9,
    Enter: 13,
    " ": 32,
    Escape: 27,
    End: 35,
    Home: 36,
    ArrowDown: 40,
};
const KEY_ALIAS_MAP = {
    Space: " ",
    Spacebar: " ",
};
const MODIFIER_MAP = {
    Shift: "shiftKey",
};
for (const a of Object.keys(KEY_ALIAS_MAP)) {
    if (! KEY_MAP.hasOwnProperty(KEY_ALIAS_MAP[a])) {
        throw new Error(`AppDriver has bad key config: alias '${KEY_ALIAS_MAP[a]}' doesn't reference a known key`);
    }
}

const doKeyEvent = function(type, key, $el = document.activeElement) {
    const init = {
        cancelable: true,
        bubbles: true,
    };
    if (key.indexOf("-") > 0) {
        // there are modifiers
        const parts = key.split("-");
        key = parts.pop();
        parts.forEach(it => {
            if (MODIFIER_MAP.hasOwnProperty(it)) {
                init[MODIFIER_MAP[it]] = true;
            } else {
                throw new Error(`Unrecognized '${it}' modifier; augment AppDriver?`);
            }
        });
    }
    const code = (() => {
        if (KEY_ALIAS_MAP.hasOwnProperty(key)) {
            key = KEY_ALIAS_MAP[key];
        }
        if (KEY_MAP.hasOwnProperty(key)) {
            return KEY_MAP[key];
        } else if (key.length === 1) {
            return key.charCodeAt(0);
        } else {
            throw new Error(`Unrecognized '${key}' key value; augment AppDriver?`);
        }
    })();
    let simulateMethod;
    if (type === "keydown") {
        simulateMethod = "keyDown";
    } else if (type === "keyup") {
        simulateMethod = "keyUp";
    } else if (type === "keypress") {
        simulateMethod = "keyPress";
    } else {
        console.warn(`non-react-simulatable '${type}' event; augment AppDriver?`);
    }
    ReactTestUtils.Simulate[simulateMethod]($el, {
        ...init,
        key,
        keyCode: code,
    });
};

const assertSingle = $el => {
    if ($el.length !== 1) {
        console.error("Expected a single element; got", $el);
        throw new Error(`Expected a single element; got ${$el.length}. The matched elements are available in the console.`);
    }
};

const itemsFromDropdown = (itemSelector, options) => {
    options = {
        $trigger: null, // if present, click it to open the dropdown
        closeAfter: true, // if true, close the dropdown after extracting items
        section: null, // if present, only return items from the named section
        ...options,
    };
    if (options.$trigger) {
        performClick(
            // make sure to click the correct element.
            options.$trigger.hasClass("dropdown-toggle")
                ? options.$trigger
                : options.$trigger.find(".dropdown-toggle"));
    }
    // pre-condition: the dropdown is open in the portal
    const $ddWrapper = $("body .dropdown-wrapper");
    let $optWrapper = $ddWrapper;
    if (options.section) {
        const $sec = $ddWrapper.find("li.section[data-section]")
            .filter(function () {
            return $(this).text() === options.section;
        });
        if ($sec.length > 1) assertSingle($sec);
        const key = $sec.data("section");
        $optWrapper = $ddWrapper.find(`li[data-section='${key}']`);
    }
    const items = $optWrapper.find(itemSelector);
    if (options.closeAfter) {
        // Send 'esc' key to close the dropdown
        doKeyEvent("keydown", "Escape", $ddWrapper.get(0));
    }
    return items;
};

function entityCoords($el) {
    assertSingle($el);
    const data = ($el.hasClass("entity-context")
        ? $el
        : $el.parents(".entity-context"))[0].dataset;
    const ek = data.entityKey;
    const id = data.id;
    if (!ek || !id) {
        throw new Error($el.selector + " is not within a entity context");
    }
    return [ek, id];
}

const actions = {

    click($el) {
        assertSingle($el);
        switch ($el.get(0).dataset.controlType) {
            case "button":
            case "valuemutationbutton":
                $el = $el.find("button");
                break;
            case "checkbox":
                $el = getVisibleInput($el);
                break;

            case "mappingrow":
                $el = $el.find(".toggle-state");
                break;
            default:
                // just leave it alone
        }

        performClick($el);
    },

    doubleClick($el) {
        assertSingle($el);
        performClick($el, "dblclick");
    },

    focus($el) {
        assertSingle($el);
        // todo: it seems that we should only allow focusing enabled stuff?
        if ($el.is(".control")) {
            const $elem = $el.find("input, select, textarea, button").first();
            $elem.focus();
            ReactTestUtils.Simulate.focus($elem.get(0));
        } else if ($el.is("a[href], a[tabindex], :input") && $el.is(":visible") && ! $el.is(":disabled")) {
            $el.focus();
        } else {
            throw new Error(`${$el.selector} can't be focused; augment AppDriver?`);
        }
    },

    type($el, text) {
        assertSingle($el);
        switch ($el.get(0).dataset.controlType) {
            case "calendar":
            case "spinner":
                performChange($el.find("input"), text);
                break;
            case "text":
            case "calendar_masked":
                performChange($el, text);
                break;
            case "typeahead":
                const $input = $el.find("input.form-control");
                performChange($input, text); // open dropdown and set filter
                break;
            default:
                throw new Error($el.selector + " can't be typed into. (type " + $el.get(0).dataset.controlType + ")");
        }
    },

    press($el, key) {
        assertSingle($el);
        actions.focus($el);
        doKeyEvent("keydown", key);
    },

    scrollTo($el, top, left) {
        assertSingle($el);
        $el[0].scrollTop = top;
        $el[0].scrollLeft = left;
    },

    clear($el) {
        assertSingle($el);
        switch ($el.get(0).dataset.controlType) {
            case "calendar":
            case "spinner":
                performChange($el, undefined);
                break;
            case "text":
            case "calendar_masked":
                performChange($el, undefined);
                break;
            case "typeahead":
                performClick($el.find(".item-selection-close, .clear-value"));
                break;
            default:
                throw new Error($el.selector + " can't be cleared. (type " + $el.get(0).dataset.controlType + ")");
        }
    },

    clearConsole(ignored) {
        console.clear();
    },

    uploadFile($el, filename) {
        assertSingle($el);
        actions.uploadFileWithContent($el,
            filename,
            "Hello! This is a dummy file created by AppDriver!");
    },

    uploadFileWithContent($el, filename, content) {
        assertSingle($el);
        switch ($el.get(0).dataset.controlType) {
            case "manager":
                const $elem = $el.find("input").first();
                ReactTestUtils.Simulate.focus($elem.get(0));
                const files = [
                    new File([content], filename, {
                        type: "text/plain",
                        lastModified: new Date().getTime()
                    })
                ];
                ReactTestUtils.Simulate.change($elem.get(0), {
                        target: {
                            files: files
                        }
                    });
                $elem.change();
                break;
            default:
                throw new Error($el.selector + " can't upload files. (type " + $el.get(0).dataset.controlType + ")");
        }
    },

    violateOptlock($el, username) {
        const [ek, id] = entityCoords($el);
        const tk = entityMetaStore.getInfo(ek).tableKey;
        lockClient.updateRecordOnServer(tk, id, username);
    },

    openInNewClient($el, username) {
        const [ek, id] = entityCoords($el);
        const tk = entityMetaStore.getInfo(ek).tableKey;
        lockClient.openRecordInNewClient(tk, id, username);
    },

    closeOtherClient($el, username) {
        const [ek, id] = entityCoords($el);
        const tk = entityMetaStore.getInfo(ek).tableKey;
        lockClient.closeRecordInOtherClient(tk, id, username);
    },

    // implemented in the interpreter, but declared here for validation
    pause(ignored) { throw new Error("Native Implementation"); },
    exit(ignored, message) { throw new Error("Native Implementation"); },
    close(ignored) { throw new Error("Native Implementation"); },
};

function workOnSelectElement($el, text) {
    const $opt = $el.find("option").filter(function () {
        return $(this).text() === text;
    });
    assertSingle($opt);
    performChange($el, $opt.val());
}

actions.select = function($el, optionalSection, text) {
    if (arguments.length === 2) {
        text = optionalSection;
        optionalSection = null;
    }
    assertSingle($el);
    switch ($el.get(0).dataset.controlType) {
        case "bootstrap-dropdown":
            const item = itemsFromDropdown("a", {
                $trigger: $el,
                closeAfter: false,
                section: optionalSection,
            })
                .filter(function () {
                    return $(this).text().trim() === text;
                });
            assertSingle(item);
            performClick(item);
            break;

        case "typeahead":
            if (optionalSection) {
                throw new Error("Typeaheads don't have sections");
            }
            const $input = $el.find("input.form-control");
            $input.focus();
            if (typeof text === "string") { // the normal case
                performChange($input, text); // open dropdown and set filter
            }
            else {
                doKeyEvent("keydown", "ArrowDown", $input.get(0)); // open the dropdown
                for (let i = parseInt(text); i > 1; i--) {
                    doKeyEvent("keydown", "ArrowDown", $("body .dropdown-wrapper").get(0));
                }
            }
            // fake enter key
            doKeyEvent("keydown", "Enter", $("body .dropdown-wrapper").get(0));
            // close the dropdown menu
            doKeyEvent("keydown", "Escape", $("body .dropdown-wrapper").get(0));
            break;

        case "dropdown":
        case "recordbrowser":
            if (optionalSection) {
                throw new Error("Dropdowns/record browsers don't have sections");
            }
            $el = $el.find("select");
            workOnSelectElement($el, text);
            break;
        default:
            if ($el.get(0).nodeName === "SELECT") {
                workOnSelectElement($el, text);
            } else {
                throw new Error(`non-selectable element ${$el.selector}`);
            }
    }
};
actions.select.meta = {
    optionalArgCount: 1,
};

const hasClass = ($el, className) =>
    !!$el.hasClass(className);

const inspections = {

    text($el) {
        assertSingle($el);
        return $el.text();
    },

    placeholder($el) {
        assertSingle($el);
        return $el.find("input").attr("placeholder");
    },

    active($el) {
        if ($el.length === 1) {
            return hasClass($el, "active");
        }
        // looking for an index in the nodelist...
        const idxs = [];
        for (let i = 0; i < $el.length; i++) {
            if (hasClass($($el.get(i)), "active")) {
                idxs.push(i);
            }
        }
        if (idxs.length === 0) {
            return 0;
        } else if (idxs.length === 1) {
            return idxs[0] + 1;
        } else {
            throw new Error("Multiple active items were found.");
        }
    },

    value($el) {
        assertSingle($el);
        let v = null;
        let item = null;
        switch ($el.get(0).dataset.controlType) {
            case "bootstrap-dropdown":
                v = $el.find(".dropdown-toggle .pull-left").text();
                break;
            case "typeahead":
                v = $el.find(".item-selection,.single-item-selection")
                    .clone()
                    .children()
                    .remove()
                    .end()
                    .map(function() {
                        return $(this).text();}).get()
                    .join(",");

                return v;
            case "choice":
            case "dropdown":
            case "recordbrowser":
                item = getVisibleInput($el);
                v = item.find("option:selected").text();
                break;
            case "text":
            case "calendar":
            case "spinner":
                item = getVisibleInput($el);
                v = item.val();
                break;
            case "label":
                if ($el.is(".file-field")) {
                    v = $el.find(".file-link")
                        .map(function() {
                            return $(this).text();}).get()
                        .join(",");
                } else {
                    v = $el.text();
                }
                break;
            case "validationindicator":
                v = $el.get(0).dataset.controlValue;
                break;
            case "validationfilter":
                v = $el.is(".checked");
                break;
            case "mappingrow":
                v = $el.find(".toggle-state").is(".checked");
                break;
            case "checkbox":
                item = getVisibleInput($el);
                v = item.is(":checked");
                break;
            default:
                throw new Error(`non-valued element ${$el.selector}`);
        }

        return v;
    },

    rawValue($el) {
        assertSingle($el);
        if ($el.is(":input")) {
            return $el.val();
        }

        if ($el.is(".control")) {
            return $el.find("input, select, textarea, button").first().val();
        }

        throw new Error(`non-valued element ${$el.selector}`);
    },

    focused($el) {
        assertSingle($el);
        if ($el.is(".control")) {
            const item = getVisibleInput($el);
            return item.is(":focus") || (item.get(0) === document.activeElement);
        }

        return $el[0] === document.activeElement;
    },

    enabled($el) {
        assertSingle($el);
        if ($el.is(".control")) {
            /*
             * This used to switch on type and do different stuff, but now that
             * jsclient itself relies on a zero-arg invocation of "enabled"
             * acting as a getter, AppDriver should follow suit.
             */
            return $el.find("input, select, textarea, button").first().is(":enabled");

        } else {
            return $el.is(":enabled");
        }
    },

    disabled($el) {
        console.log("the 'disabled' inspection is deprecated. use 'enabled' w/ `false`.");
        return ! inspections.enabled($el);
    },

    visible($el) {
        assertSingle($el);
        // this may or may not be clever, but it appears to work pretty well,
        // and is certainly more representative than what jQuery does.
        function isInView(el) {
            function isDescendant(n) {
                do {
                    if (n === el) {
                        return true;
                    }
                    n = n.parentElement;
                } while (n != null);
                return false;
            }
            // getBoundingClientRect is a draft, but widely supported...
            const r = el.getBoundingClientRect();
            const x = r.left + r.width / 2;
            const y = r.top + r.height / 2;
            if (x < 0 || x > window.innerWidth) {
                return false;
            }
            if (y < 0 || y > window.innerHeight) {
                return false;
            }
            const itUi = document.getElementById("IT-UI");
            itUi && (itUi.style.display = "none");
            try {
                // elementFromPoint is experimental, but widely supported...
                return isDescendant(document.elementFromPoint(x, y));
            } finally {
                itUi && (itUi.style.display = "block");
            }
        }

        const visible = $el.is(":visible");
        if ($el.is(".dropdown-helper-placeholder")) {
            // DropdownHelper left this node for targeting, but it's a cheat.
            // However, DropdownHelper's entire purpose is to ensure that helped
            // dropdowns are the only element available for interaction. Which
            // is even stronger than "visible", so just trust it. Unit tests
            // will bomb out if it's doing stuff wrong.
            return visible;
        }
        return visible && isInView($el[0]);
    },

    isPartial($el) {
        assertSingle($el);
        switch ($el.get(0).dataset.controlType) {
            case "typeahead": {
                return $el.find(".typeahead").get(0).dataset.partial === "true";
            }
            default:
                return $el.get(0).dataset.partial === "true";
        }
    },

    isLoading($el) {
        assertSingle($el);
        switch ($el.get(0).dataset.controlType) {
            case "typeahead": {
                return $el.find(".typeahead").get(0).dataset.loading === "true";
            }
            default:
                return $el.get(0).dataset.loading === "true";
        }
    },

    isOpen($el) {
        assertSingle($el);
        return hasClass($el, "open");
    },

    isRequired($el) {
        assertSingle($el);
        return hasClass($el, "required");
    },

    isIllegal($el) {
        assertSingle($el);
        return hasClass($el, "illegal");
    },

    count($el) {
        switch ($el.get(0).dataset.controlType) {
            case "typeahead": {
                return parseInt($el.find(".typeahead").get(0).dataset.count, 10);
            }
            default:
                return $el.length;
        }
    },

    scrollTop($el) {
        assertSingle($el);
        return $el[0].scrollTop;
    },

    scrollLeft($el) {
        assertSingle($el);
        return $el[0].scrollLeft;
    },

    checked($el) {
        assertSingle($el);
        return hasClass($el, "checked");
    },

    // implemented in the interpreter, but declared here for validation
    exists(ignored) { throw new Error("Native Implementation"); },

};

// Whether the element has the named option
inspections.hasOption = function($el, optionalSection, option) {
    if (arguments.length === 2) {
        option = optionalSection;
        optionalSection = null;
    }
    assertSingle($el);
    let options = null;
    const fromPortal = (itemSelector, options) =>
        itemsFromDropdown(itemSelector, options)
            .map(function () {
                return $(this).text().trim();
            }).toArray();
    switch ($el.get(0).dataset.controlType) {
        case "bootstrap-dropdown": {
            options = fromPortal("a", {
                $trigger: $el,
                section: optionalSection,
            });
            break;
        }
        case "typeahead": {
            if (optionalSection) {
                throw new Error("Typeaheads don't have sections");
            }
            const $input = $el.find("input.form-control");
            $input.focus();
            doKeyEvent("keydown", "ArrowDown", $input.get(0));
            options = fromPortal(".typeahead-dropdown tr td");
            break;
        }
        case "recordbrowser":
        case "dropdown":
            if (optionalSection) {
                throw new Error("Record browsers don't have sections");
            }
            options = $el.find("select option")
                .map(function() {
                    return $(this).text();}).toArray();
            break;
        default:
            console.warn(`non-choice element ${$el.selector} can NEVER have options?`);
            return false;
    }
    options = options.filter(it => it === option);
    if (options.length > 1) {
        throw new Error(`Multiple '${option}' options present?!`);
    }
    return options.length === 1;
};
inspections.hasOption.meta = {
    optionalArgCount: 1,
};

export default {
    actions,
    inspections,
    elements,
};



// WEBPACK FOOTER //
// ./app/lib/AppDriver.js